import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { useSpin } from '../context/SpinContext';
import styles from './UpdateSpinModal.module.css';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const UpdateSpinModal = ({ show, handleClose, spin, onUpdate, betType }) => {
  const { updateSpin, fetchSpins } = useSpin();
  const [spinValue, setSpinValue] = useState('');
  const [outcome, setOutcome] = useState('');
  const [betAmt, setBetAmt] = useState('');
  const [showSpinGrid, setShowSpinGrid] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [customBetAmount, setCustomBetAmount] = useState(null);

  // Create the new bet options array - moved outside of component to avoid recreating
  const betOptions = useMemo(() => {
    const options = [];
    
    // Add fractional values: 0.5, 0.75, 1, 1.5, 2, 2.5
    options.push(0.5, 0.75, 1, 1.5, 2, 2.5);
    
    // Add integers from 3 to 59
    for (let i = 3; i <= 58; i++) {
      options.push(i);
    }
    
    // Add custom option
    options.push('custom');
    
    return options;
  }, []);

  const resetValues = useCallback(() => {
    setSpinValue(spin.spin_value);
    let initialBetAmt = '';
    let initialOutcome = '---';
    
    if (betType === 'rep' || betType === 'neighbors') {
      if (spin[`${betType}Bet`] && spin[`${betType}Bet`] !== 0) {
        initialOutcome = spin[`${betType}Outcome`] === 'win' ? 'W' :
                        spin[`${betType}Outcome`] === 'loss' ? 'L' : 
                        spin[`${betType}Outcome`];
        initialBetAmt = Math.abs(spin[`${betType}Bet`]).toString();
      }
    } else {
      initialOutcome = spin[`${betType}Outcome`] === 'no bet' ? '---' : 
                      spin[`${betType}Outcome`] === 'win' ? 'W' :
                      spin[`${betType}Outcome`] === 'loss' ? 'L' :
                      spin[`${betType}Outcome`];
      initialBetAmt = (spin[`${betType}Outcome`] === 'no bet' || spin[`${betType}Bet`] === 0) 
        ? '' 
        : spin[`${betType}Bet`].toString();
    }

    setOutcome(initialOutcome);
    
    // Check if the bet amount is in our predefined options
    const betAmountFloat = parseFloat(initialBetAmt);
    if (betOptions.includes(betAmountFloat)) {
      setBetAmt(betAmountFloat);
      setCustomBetAmount(null);
    } else if (betAmountFloat > 0) {
      // If it's not in our options and is greater than 0, treat as custom
      setCustomBetAmount(betAmountFloat);
      setBetAmt('custom');
    } else {
      setBetAmt('');
      setCustomBetAmount(null);
    }

    setErrors({});
    setShowSpinGrid(false);
  }, [spin, betType, betOptions]);

  useEffect(() => {
    if (show && spin) {
      resetValues();
    }
  }, [show, spin, betType, resetValues]);

  const handleSpinValueChange = useCallback((value) => {
    setSpinValue(value);
    setShowSpinGrid(false);
  }, []);

  const handleCustomBetClick = useCallback(() => {
    if (!outcome || outcome === '---') {
      setErrors({ outcome: "Please select Win or Loss first" });
      return;
    }

    const customBet = prompt('Enter custom bet amount:', customBetAmount?.toString() || '');
    if (customBet && !isNaN(customBet) && Number(customBet) > 0) {
      setCustomBetAmount(Number(customBet));
      setBetAmt('custom');
      setErrors(prev => ({ ...prev, betAmount: null }));
    } else if (customBet !== null) {
      alert('Please enter a valid number greater than 0.');
    }
  }, [customBetAmount, outcome]);

  const handleBetAmount = useCallback((amount) => {
    if (!outcome || outcome === '---') {
      setErrors({ outcome: "Please select Win or Loss first" });
      return;
    }

    if (amount === 'custom') {
      handleCustomBetClick();
    } else {
      setBetAmt(amount); // Store the actual numeric value, not as string
      setCustomBetAmount(null);
    }
    setErrors(prev => ({ ...prev, betAmount: null }));
  }, [outcome, handleCustomBetClick]);

  const handleOutcomeChange = useCallback((newOutcome) => {
    if (newOutcome === 'NB') {
      setBetAmt('0');
      setOutcome('---');
    } else {
      setOutcome(newOutcome);
      if (betAmt === '0') {
        setBetAmt('');
      }
    }
    setErrors({});
  }, [betAmt]);

  const isValidBet = useMemo(() => {
    if (outcome === '---') return true;
    if (outcome === 'W' || outcome === 'L') {
      const betAmount = betAmt === 'custom' ? customBetAmount : parseFloat(betAmt);
      return betAmount > 0;
    }
    return false;
  }, [outcome, betAmt, customBetAmount]);

  const handleSave = useCallback(async () => {
    if (!outcome || (outcome !== '---' && outcome !== 'W' && outcome !== 'L')) {
      setErrors(prev => ({ ...prev, outcome: "Please select Win, Loss, or No Bet" }));
      return;
    }
    if (!isValidBet) {
      setErrors(prev => ({ ...prev, betAmount: "Bet amount must be greater than 0 for Win or Loss" }));
      return;
    }

    try {
      const updateData = {
        spinId: spin._id,
        spin_value: spinValue,
      };

      const rawBetAmount = betAmt === 'custom' ? customBetAmount : parseFloat(betAmt) || 0;

      if (betType === 'rep' || betType === 'neighbors') {
        updateData[`${betType}Outcome`] = outcome === 'W' ? 'win' : outcome === 'L' ? 'loss' : '---';
        updateData[`${betType}Bet`] = outcome === '---' ? 0 : Math.abs(rawBetAmount);
      } else {
        updateData[`${betType}Outcome`] = outcome === 'W' ? 'win' : outcome === 'L' ? 'loss' : '---';
        updateData[`${betType}Bet`] = outcome === '---' ? 0 : rawBetAmount;
      }

      console.log("Sending update with data:", updateData);
      const response = await axios.put(`${backendUrl}/api/spins/${spin._id}`, updateData);

      if (response.status === 200) {
        console.log("Update successful, response:", response.data);
        updateSpin(response.data);
        
        await fetchSpins(spin.session_id);
        
        handleClose();
        if (onUpdate) {
          onUpdate(response.data);
        }
      }
    } catch (error) {
      console.error("Failed to update spin:", error);
      setErrors(prev => ({ ...prev, submit: "Failed to update spin. Please try again." }));
    }
  }, [spin._id, spin.session_id, spinValue, betType, outcome, betAmt, customBetAmount, updateSpin, fetchSpins, handleClose, onUpdate, isValidBet]);

  const handleDelete = useCallback(() => {
    setShowDeleteConfirmation(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    try {
      await axios.delete(`${backendUrl}/api/spins/${spin._id}`);
      await fetchSpins(spin.session_id);
      handleClose();
    } catch (error) {
      console.error("Failed to delete spin:", error);
      setErrors(prev => ({ ...prev, delete: "Failed to delete spin. Please try again." }));
    }
    setShowDeleteConfirmation(false);
  }, [spin._id, spin.session_id, fetchSpins, handleClose]);

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirmation(false);
  }, []);

  const getSpinButtonColor = useCallback((value) => {
    const redNumbers = ['1', '3', '5', '7', '9', '12', '14', '16', '18', '19', '21', '23', '25', '27', '30', '32', '34', '36'];
    if (value === '0' || value === '00') return styles.greenButton;
    return redNumbers.includes(value) ? styles.redButton : styles.blackButton;
  }, []);

  const getBetButtonClass = useCallback((value) => {
    let classes = [styles.betButton];
    
    // Handle selection logic
    if (value === 'custom' && betAmt === 'custom') {
      classes.push(styles.selected);
    } else if (value !== 'custom' && betAmt === value) {
      classes.push(styles.selected);
    }
    
    if (outcome === 'L') classes.push(styles.redButton);
    return classes.join(' ');
  }, [betAmt, outcome]);

  return (
    show && (
      <div className={styles["edit-spin-modal"]}>
        <div className={styles["edit-spin-content"]}>
          <div className={styles["scrollable-content"]}>
            <div className={styles["modal-header"]}>
              <h3>Edit Spin</h3>
              {!showSpinGrid && (
                <div 
                  className={styles["spin-value-display"]} 
                  onClick={() => setShowSpinGrid(true)}
                >
                  Current Spin: {spinValue}
                </div>
              )}
            </div>

            {showSpinGrid ? (
              <div className={styles["spin-grid"]}>
                <div className={styles["zeros-row"]}>
                  {['0', '00'].map(num => (
                    <button
                      key={num}
                      className={`${styles.spinButton} ${getSpinButtonColor(num)}`}
                      onClick={() => handleSpinValueChange(num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
                <div className={styles["numbers-grid"]}>
                  {Array.from({ length: 36 }, (_, i) => (i + 1).toString()).map(num => (
                    <button
                      key={num}
                      className={`${styles.spinButton} ${getSpinButtonColor(num)}`}
                      onClick={() => handleSpinValueChange(num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className={styles["outcome-buttons-container"]}>
                  <button
                    className={`${styles["outcome-button"]} ${outcome === 'W' ? styles["selected-win"] : ''}`}
                    onClick={() => handleOutcomeChange('W')}
                  >
                    Win
                  </button>
                  <button
                    className={`${styles["outcome-button"]} ${outcome === 'L' ? styles["selected-loss"] : ''}`}
                    onClick={() => handleOutcomeChange('L')}
                  >
                    Loss
                  </button>
                  <button
                    className={`${styles["outcome-button"]} ${outcome === '---' ? styles["selected-nb"] : ''}`}
                    onClick={() => handleOutcomeChange('NB')}
                  >
                    No Bet
                  </button>
                </div>

                {errors.outcome && <div className={styles["error-message"]}>{errors.outcome}</div>}
                
                {outcome && outcome !== '---' && (
                  <div className={styles["grid-container"]}>
                    <div className={styles["grid"]}>
                      {betOptions.map((bet, index) => {
                        if (bet === 'custom') {
                          return (
                            <button
                              key="custom"
                              className={getBetButtonClass('custom')}
                              onClick={() => handleBetAmount('custom')}
                            >
                              {customBetAmount !== null ? `${customBetAmount}` : '$'}
                            </button>
                          );
                        }
                        
                        // Format display to remove leading zero for fractional values
                        const displayValue = bet < 1 ? bet.toString().substring(1) : bet;
                        // Check if the value has a decimal point to apply smaller font
                        const hasDecimal = bet.toString().includes('.');
                        
                        return (
                          <button
                            key={bet}
                            className={`${getBetButtonClass(bet)} ${hasDecimal ? styles.decimalValue : ''}`}
                            onClick={() => handleBetAmount(bet)}
                          >
                            {displayValue}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {errors.betAmount && <div className={styles["error-message"]}>{errors.betAmount}</div>}
              </>
            )}
          </div>

          <div className={styles["button-group"]}>
            <button className={styles["btn-cancel"]} onClick={handleClose}>
              Cancel
            </button>
            {!showSpinGrid && (
              <>
                <button 
                  className={styles["btn-update"]} 
                  onClick={handleSave}
                  disabled={!isValidBet}
                >
                  Save
                </button>
                <button className={styles["btn-delete"]} onClick={handleDelete} aria-label="Delete">
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </>
            )}
          </div>
        </div>

        {showDeleteConfirmation && (
          <div className={styles["delete-confirmation-modal"]}>
            <div className={styles["delete-confirmation-content"]}>
              <p>Are you sure you want to delete this spin?</p>
              <div className={styles["delete-confirmation-buttons"]}>
                <button onClick={confirmDelete} className={styles["btn-confirm-delete"]}>Yes, Delete</button>
                <button onClick={cancelDelete} className={styles["btn-cancel-delete"]}>No, Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  );
};

export default UpdateSpinModal;